import { useMemo, useState } from 'react';
import {
  makeStyles,
  tokens,
  Body2,
  Caption1,
  Badge,
  Input,
  Dropdown,
  Option,
  Button,
} from '@fluentui/react-components';
import { PageTitle } from '@/components/PageTitle';
import { ArrowDownload24Regular } from '@fluentui/react-icons';
import { saveAs } from 'file-saver';
import { DataTable, type Column } from '@/components/DataTable';
import { useSharePointList } from '@/hooks/useSharePointList';
import type { ChangeLog, ChangeAction, SPListItem } from '@/models/types';
import { useAppContext } from '@/store/appContext';
import { canViewAuditLog } from '@/utils/permissions';
import { AccessDenied } from '@/components/AccessDenied';
import { formatDateTime } from '@/utils/formatters';

const ACTION_COLORS: Record<ChangeAction, 'brand' | 'success' | 'warning' | 'danger' | 'informative'> = {
  Created: 'success',
  Updated: 'brand',
  Deleted: 'danger',
  Synced: 'informative',
  Assigned: 'success',
  Revoked: 'warning',
  Approved: 'success',
  Rejected: 'danger',
};

const useStyles = makeStyles({
  page: { display: 'flex', flexDirection: 'column', gap: '20px' },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap' as const,
    gap: '12px',
  },
  toolbar: {
    display: 'flex',
    gap: '12px',
    flexWrap: 'wrap' as const,
    alignItems: 'center',
  },
  metrics: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '12px',
    '@media (max-width: 480px)': {
      gridTemplateColumns: 'repeat(2, 1fr)',
    },
  },
  metricCard: {
    padding: '16px',
    backgroundColor: tokens.colorNeutralBackground1,
    borderRadius: tokens.borderRadiusLarge,
    boxShadow: tokens.shadow2,
  },
  metricValue: {
    fontSize: '24px',
    fontWeight: 700,
    color: tokens.colorBrandForeground1,
  },
});

export function AuditLog() {
  const styles = useStyles();
  const { currentUser, listIds } = useAppContext();

  if (currentUser && !canViewAuditLog(currentUser.role)) {
    return <AccessDenied reason="Audit log is restricted to Administrators only." />;
  }

  const { items: logs, loading, refresh } = useSharePointList<ChangeLog>(
    'OC_ChangeLog',
    { autoFetch: !!listIds, top: 1000, orderBy: 'fields/Timestamp desc' },
  );

  const [entityFilter, setEntityFilter] = useState('all');
  const [actionFilter, setActionFilter] = useState('all');
  const [dateRange, setDateRange] = useState<{ from: string; to: string }>({
    from: '',
    to: '',
  });

  const uniqueEntities = useMemo(() => {
    const set = new Set<string>();
    for (const l of logs) if (l.fields.Entity) set.add(l.fields.Entity);
    return Array.from(set).sort();
  }, [logs]);

  const uniqueActions = useMemo(() => {
    const set = new Set<string>();
    for (const l of logs) if (l.fields.Action) set.add(l.fields.Action);
    return Array.from(set).sort();
  }, [logs]);

  const filtered = useMemo(() => {
    return logs.filter((l) => {
      if (entityFilter !== 'all' && l.fields.Entity !== entityFilter) return false;
      if (actionFilter !== 'all' && l.fields.Action !== actionFilter) return false;
      if (dateRange.from) {
        const ts = new Date(l.fields.Timestamp).getTime();
        if (ts < new Date(dateRange.from).getTime()) return false;
      }
      if (dateRange.to) {
        const ts = new Date(l.fields.Timestamp).getTime();
        const toDate = new Date(dateRange.to);
        toDate.setHours(23, 59, 59);
        if (ts > toDate.getTime()) return false;
      }
      return true;
    });
  }, [logs, entityFilter, actionFilter, dateRange]);

  const metrics = useMemo(() => {
    const last24h = Date.now() - 24 * 60 * 60 * 1000;
    const last24hCount = logs.filter(
      (l) => new Date(l.fields.Timestamp).getTime() > last24h,
    ).length;
    const loginCount = logs.filter(
      (l) =>
        l.fields.Entity === 'Auth' && l.fields.Details?.startsWith('Login'),
    ).length;
    const uniqueUsers = new Set(logs.map((l) => l.fields.ChangedBy)).size;
    return {
      total: logs.length,
      last24h: last24hCount,
      logins: loginCount,
      uniqueUsers,
    };
  }, [logs]);

  const handleExport = () => {
    const header = 'Timestamp,Action,Entity,EntityName,Details,ChangedBy\n';
    const rows = filtered
      .map((l) => {
        const f = l.fields;
        const escape = (s: string) => `"${(s ?? '').replace(/"/g, '""')}"`;
        return [
          f.Timestamp,
          f.Action,
          f.Entity,
          escape(f.EntityName ?? ''),
          escape(f.Details ?? ''),
          f.ChangedBy,
        ].join(',');
      })
      .join('\n');
    const blob = new Blob([header + rows], { type: 'text/csv' });
    saveAs(blob, `prism-audit-log-${new Date().toISOString().split('T')[0]}.csv`);
  };

  const columns: Column<SPListItem<ChangeLog>>[] = [
    {
      key: 'ts',
      label: 'When',
      sortable: true,
      getValue: (item) => item.fields.Timestamp,
      render: (item) => (
        <Caption1>{formatDateTime(item.fields.Timestamp)}</Caption1>
      ),
    },
    {
      key: 'action',
      label: 'Action',
      sortable: true,
      getValue: (item) => item.fields.Action,
      render: (item) => (
        <Badge
          appearance="filled"
          color={ACTION_COLORS[item.fields.Action] ?? 'informative'}
          size="small"
        >
          {item.fields.Action}
        </Badge>
      ),
    },
    {
      key: 'entity',
      label: 'Entity',
      sortable: true,
      getValue: (item) => item.fields.Entity,
      render: (item) => (
        <Badge appearance="outline" size="small">
          {item.fields.Entity}
        </Badge>
      ),
    },
    {
      key: 'name',
      label: 'Subject',
      getValue: (item) => item.fields.EntityName,
      render: (item) => <Body2>{item.fields.EntityName}</Body2>,
    },
    {
      key: 'details',
      label: 'Details',
      getValue: (item) => item.fields.Details,
      render: (item) => (
        <Caption1 style={{ maxWidth: 400, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' as const, display: 'block' }}>
          {item.fields.Details}
        </Caption1>
      ),
    },
    {
      key: 'by',
      label: 'By',
      sortable: true,
      getValue: (item) => item.fields.ChangedBy,
      render: (item) => (
        <Caption1>{item.fields.ChangedBy?.split('@')[0] ?? '—'}</Caption1>
      ),
    },
  ];

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <PageTitle>Audit Log</PageTitle>
        <div style={{ display: 'flex', gap: 8 }}>
          <Button appearance="outline" onClick={refresh}>Refresh</Button>
          <Button
            appearance="primary"
            icon={<ArrowDownload24Regular />}
            onClick={handleExport}
          >
            Export CSV
          </Button>
        </div>
      </div>

      <div className={styles.metrics}>
        <div className={styles.metricCard}>
          <Caption1>Total Events</Caption1>
          <div className={styles.metricValue}>{metrics.total}</div>
        </div>
        <div className={styles.metricCard}>
          <Caption1>Last 24 Hours</Caption1>
          <div className={styles.metricValue}>{metrics.last24h}</div>
        </div>
        <div className={styles.metricCard}>
          <Caption1>Login Events</Caption1>
          <div className={styles.metricValue}>{metrics.logins}</div>
        </div>
        <div className={styles.metricCard}>
          <Caption1>Unique Actors</Caption1>
          <div className={styles.metricValue}>{metrics.uniqueUsers}</div>
        </div>
      </div>

      <div className={styles.toolbar}>
        <Dropdown
          placeholder="Entity"
          value={entityFilter === 'all' ? 'All entities' : entityFilter}
          onOptionSelect={(_, d) => setEntityFilter(d.optionValue as string)}
          style={{ minWidth: 160 }}
        >
          <Option value="all">All entities</Option>
          {uniqueEntities.map((e) => (
            <Option key={e} value={e}>
              {e}
            </Option>
          ))}
        </Dropdown>
        <Dropdown
          placeholder="Action"
          value={actionFilter === 'all' ? 'All actions' : actionFilter}
          onOptionSelect={(_, d) => setActionFilter(d.optionValue as string)}
          style={{ minWidth: 140 }}
        >
          <Option value="all">All actions</Option>
          {uniqueActions.map((a) => (
            <Option key={a} value={a}>
              {a}
            </Option>
          ))}
        </Dropdown>
        <Input
          type="date"
          value={dateRange.from}
          onChange={(_, d) => setDateRange((r) => ({ ...r, from: d.value }))}
          contentBefore={<Caption1>From</Caption1>}
          style={{ minWidth: 180 }}
        />
        <Input
          type="date"
          value={dateRange.to}
          onChange={(_, d) => setDateRange((r) => ({ ...r, to: d.value }))}
          contentBefore={<Caption1>To</Caption1>}
          style={{ minWidth: 180 }}
        />
      </div>

      <DataTable
        columns={columns}
        items={filtered}
        loading={loading}
        searchPlaceholder="Search audit log..."
        searchFields={(item) =>
          `${item.fields.Action} ${item.fields.Entity} ${item.fields.EntityName} ${item.fields.Details} ${item.fields.ChangedBy}`
        }
        getRowKey={(item) => item.id}
        emptyMessage="No audit events match your filters."
        renderMobile={(item) => ({
          title: `${item.fields.Action} · ${item.fields.Entity}`,
          subtitle: item.fields.EntityName || item.fields.Details,
          meta: `${item.fields.ChangedBy} · ${formatDateTime(item.fields.Timestamp)}`,
        })}
      />
    </div>
  );
}
